import { css } from "@emotion/react";
import { useCallback } from "react";
import { AlertMessageType } from "../../Alert";
import { CSVImportUIColumns, CellError, Mode } from "../CSVImportUI";
import { CSVImportUITable } from "../CSVImportUITable";
import { ScreenLayout } from "../ScreenLayout";
import { SubmitProgress } from "../SubmitProgress";
import { useNavigate } from "../ScreenProvider";
import { ScreenProps } from "./types";

export interface UploadCSVScreenProps<Row> extends ScreenProps {
  data: Row[];
  headers: string[];
  errors: ([string, string][] | undefined)[];
  onSubmitComplete: (data: Row[], errors: (CellError[] | undefined)[]) => void;
  mode: Mode;
  /**
   * CSVをパースしたデータのアップロードがトリガーされた際に呼ばれる。
   * 行ごとにデータをアップロードする。
   * onUploadDataが指定されている場合、こちらは無視される。
   */
  onUploadDataByRow?: (args: {
    index: number;
    /**
     * 欠損したフィールドがある可能性があるため、型を緩くする。
     */
    row: Partial<Row>;
    mode: Mode;
  }) => Promise<{
    /**
     * @deprecated 代わりに `errors` を使用してください。
     */
    error?: CellError;
    errors?: CellError[];
    data?: Record<string, string>;
  }>;
  /**
   * CSVをパースしたデータのアップロードがトリガーされた際に呼ばれる。
   * 一括でデータをアップロードする。
   */
  onUploadData?: (args: { data: Partial<Row>[]; mode: Mode }) => Promise<{
    errorMessage?: AlertMessageType;
    data?: Record<string, string>[];
  }>;
  columns?: CSVImportUIColumns;
  title?: string;
}

export const UploadCSVScreen = <Row extends Record<string, string>>({
  data,
  errors,
  headers,
  onSubmitComplete,
  mode,
  onUploadData,
  onUploadDataByRow,
  columns,
  title = "アップロード",
}: UploadCSVScreenProps<Row>) => {
  const { onNavigate } = useNavigate();

  const handleUpload = useCallback(
    async (data: Row[]) => {
      const result = await onUploadData?.({ data, mode });
      return result ?? {};
    },
    [mode, onUploadData],
  );

  const handleUploadByRow = useCallback(
    async (index: number, row: Row) => {
      const result = await onUploadDataByRow?.({
        index,
        row,
        mode,
      });
      return result ?? {};
    },
    [mode, onUploadDataByRow],
  );
  return (
    <ScreenLayout title={title}>
      <div
        css={css`
          display: grid;
          gap: 16px;
        `}
      >
        <CSVImportUITable
          columns={columns}
          rows={data}
          errors={errors}
          headers={headers}
        />

        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <SubmitProgress
            mode={onUploadData ? "all" : "byRow"}
            label="アップロード"
            data={data}
            errors={errors}
            onSubmitComplete={onSubmitComplete}
            onSubmit={handleUpload}
            onSubmitByRow={handleUploadByRow}
            onFinish={() => {
              onNavigate("complete");
            }}
            successMessage="アップロードが完了しました"
            onConfirm={() => {
              return window.confirm(
                `${data.length}件のデータをアップロードします。よろしいですか？`,
              );
            }}
          />
        </div>
      </div>
    </ScreenLayout>
  );
};
